'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  FloatingList,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { MenuRootContext } from './context/menu-root.context';
import { menuContentPositionerStyles, menuContentRecipe } from './styles/menu-content.styles';
import type { MenuContentPropsInterface } from './types/menu-content.types';

/**
 * @since 10.0.0
 */
export const MenuContent = forwardRef<HTMLDivElement, MenuContentPropsInterface>(
  (props: MenuContentPropsInterface, forwardedRef) => {
    const { dividers, menu } = useContext(MenuRootContext);
    const {
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const contentPositionerStyles = css(menuContentPositionerStyles);
    const contentStyles = css(menuContentRecipe.raw({ dividers }), cssProp);

    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: ['floating', 'content'],
      modal: false,
      visuallyHiddenDismiss: 'Dismiss menu',
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([menu.refs.setFloating, forwardedRef]);

    return (
      <FloatingFocusManager context={menu.context} {...focusManagerProps}>
        <FloatingList elementsRef={menu.elementsRef}>
          <div
            ref={mergedRefs}
            data-c11n-version={__C11N_VERSION__}
            data-c11n-component="Menu.ContentPositioner"
            aria-labelledby={menu.labelId}
            className={cx('c11n', contentPositionerStyles)}
            style={{
              ...menu.floatingStyles,
              visibility: menu.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
            }}
            {...menu.getFloatingProps(rest)}
          >
            <div
              data-c11n-version={__C11N_VERSION__}
              data-c11n-component="Menu.Content"
              className={cx('c11n', contentStyles, className)}
              style={{ ...menu.floatingTransitionStyles, ...style }}
            >
              {children}
            </div>
          </div>
        </FloatingList>
      </FloatingFocusManager>
    );
  },
);

MenuContent.displayName = 'Menu.Content';
